import clsx from 'clsx'
import { useState, useMemo } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { globalState } from '@/components/helpers/utils'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { normalisedArray } from 'libs/searchspring/helpers/normalisers'
import Icon from '@/components/snippets/icon/icon'

const getStockStatus = (variant, stockLevels) => {
  if (!variant) return { inStock: false, text: '' }
  const quantity = variant.inventory_quantity
  const zeroStockLevel = stockLevels.find((stock) => stock.max_quantity === 0)

  if (quantity <= 0 && zeroStockLevel) {
    return {
      inStock: false,
      text: zeroStockLevel.message,
      warning: true,
      stockLevel: quantity,
      textColour: zeroStockLevel.message_colour
    }
  }

  if (quantity <= 0) {
    return { inStock: false, text: 'Out of stock', warning: true, stockLevel: quantity, textColour: '#b3b3b3' }
  }

  for (const stock of stockLevels) {
    if (quantity <= stock.max_quantity) {
      return {
        inStock: true,
        text: stock.message,
        warning: true,
        stockLevel: quantity,
        textColour: stock.message_colour
      }
    }
  }

  return {
    inStock: true,
    text: '',
    warning: false,
    stockLevel: quantity,
    text_color: ''
  }
}

export function MinicartCard({ result, className, dataIndex = '0' }) {
  const { currentResult } = useSwatches(result)
  const [selectedSize, setSelectedSize] = useState('')
  const [loading, setLoading] = useState(false)
  const clickLogger = useBeaconLogClick()

  const criteria = useMemo(() => ({
    tags: normalisedArray(currentResult?.tags) ?? [],
    collections: normalisedArray(currentResult?.collection_handle) ?? [],
    products: [currentResult.handle]
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  const [firstBadge, secondBadge] = useBadges(criteria)
  const [firstPromoline, secondPromoline] = usePromolines(criteria)

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  const handleAddToCart = async () => {
    const targetVariant = currentResult.variants?.find((v) => v.option1 === selectedSize || v.label === selectedSize)
    if (!targetVariant) return

    if (window.Cart) {
      setLoading(true)
      try {
        await window.Cart.addLineItems({
          id: targetVariant.id,
          quantity: 1
        })
        setSelectedSize('')
      } catch (error) {
        console.error('Error adding item to cart:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
  const getStatusForSize = (size) => {
    const variant = currentResult.variants?.find((v) => v.option1 === size || v.label === size)
    return getStockStatus(variant, stockLevels)
  }

  return (
    <div
      className={clsx('group/card relative flex rounded-sm border border-neutral-light bg-white', className)}
      id={`result__${currentResult.handle}`}
      data-dl-track="select_item"
      data-dl-product-id={currentResult.uid}
      data-dl-index={dataIndex}
    >
      <div className="flex w-full">
        <div className="overflow-hidden relative aspect-2/3 w-1/4 shrink-0">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult}>
              {(firstBadge || secondBadge) && (
                <div className="absolute flex max-w-[80%] flex-col gap-y-0.125">
                  {firstBadge && <Badge badge={firstBadge} />}
                  {secondBadge && <Badge badge={secondBadge} />}
                </div>
              )}
            </ResultImage>
          </a>
        </div>

        <div className="flex w-3/4 flex-col justify-between gap-y-0.5 p-0.5">
          <div>
            <a href={url} onClick={handleLogClick} className="paragraph-s line-clamp-2 font-serif link-fade" aria-label={currentResult.name}>
              {currentResult.name}
            </a>
          </div>

          <div className="flex flex-col">
            <Price price={result.ss_price} compareAtPrice={result.ss_retail} className="flex gap-x-0.125" />
            {(firstPromoline || secondPromoline) && (
              <div className="flex flex-col gap-0.25">
                {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Minicart" promolineSize="small" />}
                {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Minicart" promolineSize="small" />}
              </div>
            )}
          </div>

          <div className="flex flex-row gap-y-0.25">
            <select
              className="w-full rounded border border-neutral-medium bg-white px-0.25 py-0.125 text-xs"
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
            >
              <option value="">Select Size</option>
              {currentResult.variants?.length > 0 ? (
                currentResult.variants.map((variant) => {
                  const sizeLabel = variant.option1 || variant.label
                  const status = getStatusForSize(sizeLabel)
                  return (
                    <option key={variant.id} value={sizeLabel} disabled={!status.inStock}>
                      {sizeLabel} {!status.inStock ? '(Out of Stock)' : ''}
                    </option>
                  )
                })
              ) : (
                <option value="" disabled>
                  No sizes available
                </option>
              )}
            </select>

            <button
              className={clsx(
                'btn flex items-center justify-center gap-x-0.25 btn-fill btn-primary btn-m',
                !selectedSize || loading ? 'cursor-not-allowed !bg-black !text-white opacity-50 btn-fill' : 'btn-fill'
              )}
              type="button"
              onClick={handleAddToCart}
              disabled={!selectedSize || loading}
              aria-label="Add To Bag"
            >
              <div className="flex flex-row flex-nowrap items-center justify-center gap-x-0.25">
                <Icon id={'plus'} className="h-[8px] w-[8px]" />
                <Icon id={'bag'} className="h-[15px] w-[15px]" />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
